import { Controller, Input, useFormContext, z } from '@qred/components-library';
import { Select } from '@qred/components-library/next';
import React from 'react';
import { ECardPurpose } from '~/enums';
import { useCardPurposeOptions } from '~/hooks/useCardPurposeOptions';
import useTranslate from '~/hooks/useTranslate';

const validateCardPurposeReason = (data: {
  purposeOfCard: ECardPurpose;
  cardPurposeManual: string;
}) => {
  if (data.purposeOfCard === ECardPurpose.OtherPurpose) {
    return data.cardPurposeManual.trim().length > 0;
  }
  return true;
};

export const cardPurposeSchema = z.object({
  cardPurpose: z
    .object({
      purposeOfCard: z.nativeEnum(ECardPurpose, {
        required_error: 'ValidationErrors.Required',
      }),
      cardPurposeManual: z.string().max(190, {
        message: 'ValidationErrors.MaxLength190',
      }),
    })
    .refine(validateCardPurposeReason, {
      message: 'ValidationErrors.Required',
      path: ['cardPurposeManual'],
    }),
});

type CardPurposeForm = z.infer<typeof cardPurposeSchema>;

export const CardPurposeSelect = () => {
  const t = useTranslate();
  const {
    control,
    resetField,
    watch,
    trigger,
  } = useFormContext<CardPurposeForm>();

  const cardPurpose = watch('cardPurpose');
  const { cardPurposeOptions, isValidOption } = useCardPurposeOptions();

  return (
    <>
      <Controller
        control={control}
        name="cardPurpose.purposeOfCard"
        render={({ field, fieldState }) => (
          <Select
            label={t('CardApplication.SelectPurposeLabel') as string}
            data={cardPurposeOptions}
            value={field.value}
            onChange={(value) => {
              if (!isValidOption(value)) {
                field.onChange(undefined);
                return;
              }
              resetField('cardPurpose', {
                defaultValue: {
                  purposeOfCard: value,
                  cardPurposeManual: '',
                },
              });
            }}
            onBlur={() => trigger('cardPurpose.purposeOfCard')}
            error={fieldState.error?.message && t(fieldState.error.message)}
            allowDeselect={false}
          />
        )}
      />

      {cardPurpose.purposeOfCard === ECardPurpose.OtherPurpose && (
        <Controller
          control={control}
          name="cardPurpose.cardPurposeManual"
          render={({ field, fieldState }) => (
            <Input
              label={t('CardApplication.SelectPurposeOtherPurposeLabel')}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message && t(fieldState.error.message)}
            />
          )}
        />
      )}
    </>
  );
};
